<template>
    <div class="coupon-form">
        <!-- 이미 적용된 쿠폰이 있는 경우 -->
        <div v-if="appliedCouponCode != null" class="applied-coupon-section">
            <v-card class="applied-coupon-card" outlined>
                <v-card-text class="pa-3">
                    <div class="d-flex justify-space-between align-center">
                        <div class="flex-grow-1">
                            <div class="d-flex align-center mb-1">
                                <v-icon small color="primary" class="mr-1">mdi-ticket-confirmation</v-icon>
                                <span class="fs-13 fw-600">{{ appliedCouponCode }}</span>
                            </div>
                            <div class="fs-12 opacity-60" v-if="appliedCouponDetails">
                                <span v-if="appliedCouponDetails.name" class="d-block">{{ appliedCouponDetails.name }}</span>
                                <span v-if="appliedCouponDetails.type === 'percentage'">{{ appliedCouponDetails.discount_value }}% {{ $t('discount') }}</span>
                                <span v-else-if="appliedCouponDetails.discount_value">
                                    {{ formatPrice(appliedCouponDetails.discount_value) }} {{ $t('discount') }}
                                </span>
                            </div>
                        </div>
                        <div class="d-flex align-center">
                            <div class="text-right mr-3" v-if="appliedCouponDiscount > 0">
                                <div class="fs-14 fw-700 primary--text">-{{ formatPrice(appliedCouponDiscount) }}</div>
                            </div>
                            <v-btn elevation="0" color="grey" class="white--text darken-3 text-capitalize px-3" small @click="removeCoupon">
                                {{ $t('change') }}
                            </v-btn>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </div>

        <!-- 쿠폰 선택 섹션 -->
        <div v-else class="coupon-selection-section">
            <!-- 쿠폰 선택 버튼 -->
            <v-row class="mt-0" :dense="!forCheckout" align="center" v-if="!showCouponList">
                <v-col cols="auto" class="fs-13 fw-600 opacity-50 flex-shrink-0" v-if="!forCheckout">
                    {{ $t('have_a_code') }}
                </v-col>
                <v-col>
                    <v-btn
                        elevation="0"
                        color="primary"
                        class="text-capitalize px-5"
                        small
                        @click="loadAvailableCoupons"
                        :loading="couponLoading"
                        :disabled="couponLoading"
                    >
                        {{ $t('select_coupon') }}
                    </v-btn>
                    <v-btn elevation="0" color="grey" class="white--text darken-3 text-capitalize px-3" small @click="showManualInput = true">
                        {{ $t('enter_code') }}
                    </v-btn>
                </v-col>
            </v-row>

            <!-- 수동 코드 입력 -->
            <v-row class="mt-0" :dense="!forCheckout" align="center" v-if="showManualInput && !showCouponList">
                <v-col :cols="forCheckout ? 8 : null" class="fw-500 opacity-80">
                    <v-text-field
                        :placeholder="$t('coupon_code')"
                        class="form-control form-control-sm white"
                        type="text"
                        v-model="couponCode"
                        hide-details="auto"
                        required
                        outlined
                    ></v-text-field>
                </v-col>
                <v-col :cols="forCheckout ? 2 : 'auto'" class="fw-700">
                    <v-btn
                        elevation="0"
                        color="grey"
                        class="white--text darken-3 text-capitalize px-3"
                        small
                        @click="applyCoupon"
                        :loading="couponLoading"
                        :disabled="couponLoading"
                    >
                        {{ $t('apply') }}
                    </v-btn>
                    <v-btn elevation="0" color="grey" class="grey--text darken-3 text-capitalize px-2" small @click="showManualInput = false" text>
                        {{ $t('cancel') }}
                    </v-btn>
                </v-col>
            </v-row>

            <!-- 쿠폰 목록 -->
            <div v-if="showCouponList" class="coupon-list mt-3">
                <div class="d-flex justify-space-between align-center mb-3">
                    <h4 class="fs-14 fw-600">{{ $t('available_coupons') }}</h4>
                    <v-btn elevation="0" color="grey" class="grey--text darken-3 text-capitalize" small @click="closeCouponList" text>{{ $t('close') }}</v-btn>
                </div>

                <!-- 사용가능한 쿠폰 -->
                <div v-if="availableCoupons.length > 0" class="mb-3">
                    <div class="fs-12 fw-600 mb-2 opacity-70">{{ $t('can_use') }} ({{ availableCoupons.length }})</div>
                    <v-card
                        v-for="coupon in availableCoupons"
                        :key="coupon.id"
                        class="coupon-card mb-2"
                        outlined
                        @click="selectCoupon(coupon)"
                        :class="{ 'coupon-card--selected': selectedCoupon && selectedCoupon.id === coupon.id }"
                    >
                        <v-card-text class="pa-3">
                            <div class="d-flex justify-space-between align-start">
                                <div class="flex-grow-1">
                                    <div class="fs-13 fw-600 mb-1">{{ coupon.name }}</div>
                                    <div class="fs-12 opacity-70 mb-2" v-if="coupon.description">{{ coupon.description }}</div>
                                    <div class="fs-11 opacity-50">
                                        <span v-if="coupon.min_order_amount">최소 {{ formatPrice(coupon.min_order_amount) }}</span>
                                        <span v-if="coupon.end_date" class="ml-2">~{{ formatDate(coupon.end_date) }}</span>
                                    </div>
                                </div>
                                <div class="text-right">
                                    <div class="fs-13 fw-700 primary--text">-{{ formatPrice(coupon.calculated_discount) }}</div>
                                    <div class="fs-11 opacity-70">{{ coupon.type === 'percentage' ? coupon.discount_value + '%' : $t('fixed_amount') }}</div>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </div>

                <!-- 사용불가능한 쿠폰 -->
                <div v-if="unavailableCoupons.length > 0">
                    <div class="fs-12 fw-600 mb-2 opacity-50">{{ $t('cannot_use') }} ({{ unavailableCoupons.length }})</div>
                    <v-card v-for="coupon in unavailableCoupons" :key="'unavailable-' + coupon.id" class="coupon-card mb-2" outlined disabled>
                        <v-card-text class="pa-3 opacity-50">
                            <div class="d-flex justify-space-between align-start">
                                <div class="flex-grow-1">
                                    <div class="fs-13 fw-600 mb-1">{{ coupon.name }}</div>
                                    <div class="fs-12 mb-2 red--text">{{ coupon.reason }}</div>
                                    <div class="fs-11">
                                        <span v-if="coupon.min_order_amount">최소 {{ formatPrice(coupon.min_order_amount) }}</span>
                                        <span v-if="coupon.end_date" class="ml-2">~{{ formatDate(coupon.end_date) }}</span>
                                    </div>
                                </div>
                                <div class="text-right">
                                    <div class="fs-13 fw-700">-{{ formatPrice(coupon.calculated_discount) }}</div>
                                    <div class="fs-11">{{ coupon.type === 'percentage' ? coupon.discount_value + '%' : $t('fixed_amount') }}</div>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </div>

                <!-- 쿠폰이 없는 경우 -->
                <div v-if="availableCoupons.length === 0 && unavailableCoupons.length === 0" class="text-center py-4 opacity-70">
                    {{ $t('no_coupons_available') }}
                </div>

                <!-- 선택된 쿠폰 적용 버튼 -->
                <div v-if="selectedCoupon" class="mt-3 pt-3 border-top">
                    <v-row>
                        <v-col>
                            <v-btn
                                elevation="0"
                                color="primary"
                                class="white--text text-capitalize"
                                block
                                @click="applySelectedCoupon"
                                :loading="couponLoading"
                                :disabled="couponLoading"
                            >
                                {{ $t('apply_coupon') }} (-{{ formatPrice(selectedCoupon.calculated_discount) }})
                            </v-btn>
                        </v-col>
                    </v-row>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapActions, mapMutations } from 'vuex';
    export default {
        props: {
            shopId: { type: Number, required: false, default: null },
            forCheckout: { type: Boolean, default: false },
        },
        data: () => ({
            couponCode: null,
            couponLoading: false,
            showCouponList: false,
            showManualInput: false,
            availableCoupons: [],
            unavailableCoupons: [],
            selectedCoupon: null,
        }),
        computed: {
            ...mapGetters('cart', [
                'getCouponCode',
                'getShopCouponDetails',
                'getShopCouponDiscount',
                'getSelectedCartIds',
                'getSelectedCartIdsByShopId',
                'getCartTotal',
                'getCartShops',
            ]),
            ...mapGetters('auth', ['isAuthenticated']),
            // 실제 샵 ID를 얻는 computed 속성
            actualShopId() {
                if (this.shopId) {
                    return this.shopId;
                }
                // 체크아웃 페이지에서 shopId가 없을 때는 첫 번째 샵의 ID 사용
                if (this.forCheckout && this.getCartShops.length > 0) {
                    return this.getCartShops[0].id;
                }
                return 6; // fallback default shop ID
            },
            // getCartShops 배열을 직접 순회하여 couponCode를 읽음
            // → Vue 2가 shop.couponCode 를 reactive dependency 로 직접 추적
            // → splice 로 배열 요소가 교체될 때 즉시 re-render 보장
            appliedCouponCode() {
                const shops = this.getCartShops;
                const shopId = this.actualShopId;
                if (!shops || !shops.length) return null;
                const shop = shopId ? shops.find((s) => s.id == shopId) : shops[0];
                return shop ? shop.couponCode || null : null;
            },
            appliedCouponDetails() {
                const shops = this.getCartShops;
                const shopId = this.actualShopId;
                if (!shops || !shops.length) return null;
                const shop = shopId ? shops.find((s) => s.id == shopId) : shops[0];
                return shop ? shop.couponDetails || null : null;
            },
            appliedCouponDiscount() {
                const shops = this.getCartShops;
                const shopId = this.actualShopId;
                if (!shops || !shops.length) return 0;
                const shop = shopId ? shops.find((s) => s.id == shopId) : shops[0];
                return shop ? shop.couponDiscount || 0 : 0;
            },
        },
        methods: {
            ...mapActions('cart', ['saveCoupon', 'resetCoupon', 'fetchCartProducts']),
            ...mapMutations('auth', ['showLoginDialog']),
            async loadAvailableCoupons() {
                if (!this.isAuthenticated) {
                    this.showLoginDialog(true);
                    return;
                }

                let actualShopId = this.actualShopId;

                this.couponLoading = true;
                try {
                    const cartItemIds = actualShopId ? this.getSelectedCartIdsByShopId(actualShopId) : this.getSelectedCartIds;
                    const orderAmount = this.getCartTotal(actualShopId);

                    const data = {
                        cart_item_ids: cartItemIds,
                        shop_id: actualShopId,
                        order_amount: orderAmount,
                    };

                    const res = await this.call_api('post', 'checkout/available-coupons', data);

                    if (res.data.success) {
                        this.availableCoupons = res.data.data.available;
                        this.unavailableCoupons = res.data.data.unavailable;
                        this.showCouponList = true;
                    } else {
                        this.snack({ message: res.data.message || '쿠폰을 불러오는데 실패했습니다.', color: 'red' });
                    }
                } catch (error) {
                    console.error('쿠폰 목록 로드 오류:', error);
                    this.snack({ message: '쿠폰을 불러오는데 실패했습니다.', color: 'red' });
                } finally {
                    this.couponLoading = false;
                }
            },
            selectCoupon(coupon) {
                this.selectedCoupon = coupon;
            },
            async applySelectedCoupon() {
                if (!this.selectedCoupon) return;

                const actualShopId = this.actualShopId;

                this.couponLoading = true;
                const data = {
                    code: this.selectedCoupon.code,
                    order_amount: this.getCartTotal(actualShopId),
                    shop_id: actualShopId,
                    cart_item_ids: actualShopId ? this.getSelectedCartIdsByShopId(actualShopId) : this.getSelectedCartIds,
                };

                try {
                    const res = await this.call_api('post', 'checkout/coupon/apply', data);

                    if (res.data.success) {
                        this.snack({ message: res.data.message });
                        // couponDiscount 포함 단일 saveCoupon 호출 → 한 번의 splice 로 처리
                        this.saveCoupon({
                            shopId: actualShopId,
                            couponCode: this.selectedCoupon.code,
                            couponDetails: res.data.coupon_details,
                            couponDiscount: res.data.discount_amount || 0,
                        });
                        this.closeCouponList();
                    } else {
                        this.snack({ message: res.data.message, color: 'red' });
                    }
                } catch (error) {
                    console.error('쿠폰 적용 오류:', error);
                    const errorMessage = error.response?.data?.message || '쿠폰 적용에 실패했습니다.';
                    this.snack({ message: errorMessage, color: 'red' });
                } finally {
                    this.couponLoading = false;
                }
            },
            async applyCoupon() {
                if (this.isAuthenticated) {
                    if (!this.couponCode) return;

                    const actualShopId = this.actualShopId;

                    this.couponLoading = true;
                    try {
                        const data = {
                            code: this.couponCode,
                            order_amount: this.getCartTotal(actualShopId),
                            shop_id: actualShopId,
                            cart_item_ids: actualShopId ? this.getSelectedCartIdsByShopId(actualShopId) : this.getSelectedCartIds,
                        };
                        const res = await this.call_api('post', 'checkout/coupon/apply', data);

                        if (res.data.success) {
                            this.snack({ message: res.data.message });
                            // couponDiscount 포함 단일 saveCoupon 호출
                            this.saveCoupon({
                                shopId: actualShopId,
                                couponCode: this.couponCode,
                                couponDetails: res.data.coupon_details,
                                couponDiscount: res.data.discount_amount || 0,
                            });
                            this.showManualInput = false;
                        } else {
                            this.snack({ message: res.data.message, color: 'red' });
                        }
                    } catch (error) {
                        console.error('수동 쿠폰 적용 오류:', error);
                        const errorMessage = error.response?.data?.message || '쿠폰 적용에 실패했습니다.';
                        this.snack({ message: errorMessage, color: 'red' });
                    } finally {
                        this.couponLoading = false;
                    }
                } else {
                    this.showLoginDialog(true);
                }
            },
            removeCoupon() {
                this.couponCode = null;
                // resetCoupon mutation에서 couponCode·couponDetails·couponDiscount 모두 초기화
                this.resetCoupon(this.actualShopId);
            },
            closeCouponList() {
                this.showCouponList = false;
                this.selectedCoupon = null;
                this.availableCoupons = [];
                this.unavailableCoupons = [];
            },
            formatPrice(price) {
                return new Intl.NumberFormat('ko-KR').format(price) + '원';
            },
            formatDate(dateString) {
                if (!dateString) return '';
                const date = new Date(dateString);
                return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
            },
        },
    };
</script>

<style lang="scss" scoped>
    .coupon-card {
        cursor: pointer;
        transition: all 0.2s ease;
        border: 1px solid #e0e0e0;
    }

    .coupon-card:hover {
        border-color: #1976d2;
        box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15);
    }

    .coupon-card--selected {
        border-color: #1976d2;
        background-color: rgba(25, 118, 210, 0.05);
    }

    .coupon-card--selected::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background-color: #1976d2;
    }

    .coupon-card[disabled] {
        cursor: not-allowed;
    }

    .applied-coupon-card {
        border-color: #1976d2;
        background-color: rgba(25, 118, 210, 0.03);
    }

    .coupon-list {
    }
</style>
